import React, { FC, PropsWithChildren, createContext, useEffect, useMemo, useState } from 'react'

import { DABCollection, getAllNFTS } from '@funded-labs/dab-js'

import {
  CK_MINTER_CANISTER_ID,
  PLUG_REGISTRY_CANISTER_ID,
  SEPOLIA_CK_MINTER_CANISTER_ID,
} from '../constants/canisterIds'

import {
  useSns,
  useProtectedCanisters,
  useCustomTokens,
  useCurrentWallet,
  useDevMode,
  useTokens,
  useLocales,
} from '../state'

import CacheClient, { DefiToken, TokenPriceWithChange } from '../clients/CacheClient'

import { CkETHMinterCanister } from '@dfinity/cketh'
import { Principal } from '@dfinity/principal'
import getAgent from '../utils/getAgent'
import { fetchEnv } from '../utils/fetchEnv'
import { logger } from '../logger'

import type { CkErc20Token } from '@dfinity/cketh/dist/candid/minter'
import {
  SNS,
  PlugToken,
  Chain,
  ICToken,
  PlugTokenGroup,
  AnalyticsProvider,
  PriceProvider,
} from '../interfaces/common'
import { Actor, ActorSubclass } from '@dfinity/agent'

import { idlFactory } from '../idls/plugTokenRegistry/plugTokenRegistry.did'
import { _SERVICE as PlugTokenRegistryFactory } from '../idls/plugTokenRegistry/plugTokenRegistry.did'

export interface ConfigProviderContextType {
  tokens: PlugToken[]
  tokenGroups: PlugTokenGroup[]
  nfts: DABCollection[]
  tokenPricesWithChange: TokenPriceWithChange
  activeSns: SNS[]
  top5Tokens: DefiToken[]
  ckerc20Tokens?: CkErc20Token[]
  ethMinterAddress?: string
  erc20MinterAddress?: string
}

export interface WeekChartItem {
  t: number
  p: number
}

export const ConfigContext = createContext<ConfigProviderContextType>(null as any)

const ConfigProvider: FC<PropsWithChildren> = ({ children }) => {
  const [top5Tokens, setTop5Tokens] = useState<DefiToken[]>([])
  const [nfts, setNfts] = useState<DABCollection[]>([])
  const [tokenPricesWithChange, setTokenPricesWithChange] = useState<TokenPriceWithChange>({})
  const [ckerc20Tokens, setCkErc20Tokens] = useState<CkErc20Token[]>()
  const [ethMinterAddress, setEthMinterAddress] = useState<string>()
  const [erc20MinterAddress, setErc20MinterAddress] = useState<string>()

  const { setSns, sns } = useSns()
  const { setProtectedCanisters } = useProtectedCanisters()
  const { currentWallet } = useCurrentWallet()
  const { customTokens } = useCustomTokens()
  const { activeNetwork, networkTokens, devEnabled } = useDevMode()
  const { setTokens, tokens, tokenGroups, setTokenGroups } = useTokens()

  const agent = useMemo(getAgent, [])
  const client = useMemo(() => new CacheClient(), [])
  const isSepolia = fetchEnv('ETH_CHAIN') === 'sepolia'

  const updateTokens = async () => {
    const actor = Actor.createActor(idlFactory, {
      canisterId: PLUG_REGISTRY_CANISTER_ID,
      agent,
    }) as ActorSubclass<PlugTokenRegistryFactory>

    const response = await actor.get_all_tokens()
    const tokens: PlugToken[] = response.map((token) => {
      return {
        id: token.id,
        chain: token.chain as Chain,
        logo: token.logo_url,
        name: token.name,
        description: token.description,
        address: token.address[0],
        standard: token.standard,
        symbol: token.symbol,
        decimals: token.decimals,
        fee: typeof token.fee[0] === 'undefined' ? undefined : Number(token.fee[0]),
        tags: token.tags,
        groupId: token.group_id[0],
        analyticsDetails: {
          analyticsProvider: token.analytics_details.analytics_provider[0] as AnalyticsProvider,
          analyticsId: token.analytics_details.analytics_id[0],
          priceProvider: token.analytics_details.price_provider[0] as PriceProvider,
          priceId: token.analytics_details.price_id[0],
        },
      }
    })

    const groupResponse = await actor.get_token_groups()
    const groups: PlugTokenGroup[] = groupResponse.map((group) => {
      return {
        id: group.id,
        name: group.name,
        logoUrl: group.logo_url,
        symbol: group.symbol,
        analyticsDetails: {
          analyticsProvider: group.analytics_details.analytics_provider[0] as AnalyticsProvider,
          analyticsId: group.analytics_details.analytics_id[0],
          priceProvider: group.analytics_details.price_provider[0] as PriceProvider,
          priceId: group.analytics_details.price_id[0],
        },
      }
    })

    setTokenGroups(groups)

    const nfts = await getAllNFTS({ agent })

    setProtectedCanisters(
      tokens.filter((t): t is ICToken => t.chain === Chain.ICP).map((t) => t.address)
    )
    setTokens(tokens)
    setNfts(nfts)
  }

  const walletCustomTokens = useMemo(() => {
    if (devEnabled) {
      return networkTokens[activeNetwork || ''] || []
    }
    return customTokens[currentWallet] || []
  }, [currentWallet, customTokens, devEnabled, activeNetwork, networkTokens])

  const allTokens = useMemo(() => {
    const mappedCustom: PlugToken[] = walletCustomTokens.map((customToken) => ({
      ...customToken,
      id: customToken.principalId,
      address: customToken.principalId,
      chain: Chain.ICP,
      tags: [],
      analyticsDetails: {},
    }))

    if (devEnabled) return mappedCustom

    return [...tokens, ...mappedCustom]
  }, [tokens, walletCustomTokens])

  const { setLocales } = useLocales()

  const refresh = async (promise: Promise<any>, setter: (val: any) => void) => {
    try {
      setter(await promise)
    } catch (e) {
      logger.debug(e as Error)
    }
  }

  const refreshData = () => {
    refresh(client.getTokenPrices(), setTokenPricesWithChange)
    refresh(client.getSns(), setSns)
    refresh(client.getTop5Tokens(), setTop5Tokens)
    refresh(client.getLocales(), setLocales)
    refreshEthData()
    updateTokens().catch(logger.debug)
  }

  const refreshEthData = async () => {
    const minterCanisterId = isSepolia ? SEPOLIA_CK_MINTER_CANISTER_ID : CK_MINTER_CANISTER_ID
    const minterCanister = CkETHMinterCanister.create({
      canisterId: Principal.from(minterCanisterId),
      agent: getAgent(),
    })

    try {
      const minterInfo = await minterCanister.getMinterInfo({ certified: true })

      setCkErc20Tokens(minterInfo.supported_ckerc20_tokens?.[0] || [])
      setErc20MinterAddress(minterInfo.erc20_helper_contract_address?.[0])
      setEthMinterAddress(minterInfo.eth_helper_contract_address?.[0])
    } catch {
      setCkErc20Tokens([])
    }
  }

  useEffect(() => {
    refreshData()

    const interval = setInterval(refreshData, 60 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  const value = useMemo(() => {
    return {
      tokens: allTokens,
      tokenGroups,
      tokenPricesWithChange,
      activeSns: sns,
      nfts,
      top5Tokens,
      ckerc20Tokens,
      ethMinterAddress,
      erc20MinterAddress,
    }
  }, [
    allTokens,
    tokenGroups,
    tokenPricesWithChange,
    sns,
    nfts,
    top5Tokens,
    ckerc20Tokens,
    ethMinterAddress,
    erc20MinterAddress,
  ])

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}

export default ConfigProvider
